import _ from 'lodash'

/**
 * Memoizes the result of a function which takes an object with id
 * The results are memoized for each id FOR AS LONG AS THE OBJECT'S REFERENCE PERSISTS
 * Once object's ref changes for a given id, its cache gets dropped.
 *
 * This approach functions identically to just memoizing by object reference,
 * but it does not leak infinite memory (the cache doesn't grow infinitely with every
 * state (ref) change, as there is just one result cached for each id at a time)
 */
export const memoizeByRefAndId = <
  IdKey extends string,
  InputObj extends { [k in IdKey]: any },
  ReturnType extends any
>(
  functionToMemoize: (obj: InputObj) => ReturnType,
  idKey: IdKey = 'id' as IdKey
) => {
  const getId = (input: InputObj) => input[idKey]

  const getLastRefForId = _.memoize((input: InputObj) => input, getId)

  const memoizedFnForId = _.memoize(functionToMemoize, getId)

  return (input: InputObj) => {
    const lastRef = getLastRefForId(input)

    if (lastRef !== input) {
      memoizedFnForId.cache.delete(getId(input))
      getLastRefForId.cache.set(getId(input), input)
    }

    return memoizedFnForId(input)
  }
}
